import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast } from "react-toastify";
import {
    FiCalendar,
    FiClock,
    FiMapPin,
    FiUser,
    FiCheckCircle,
    FiAlertCircle,
    FiSearch,
    FiRefreshCw,
    FiFolder,
    FiInfo,
    FiBriefcase,
    FiFileText,
    FiMail,
    FiPhone
} from "react-icons/fi";
import {
    MdOutlinePendingActions,
    MdOutlineCheckCircleOutline,
    MdOutlineCancel,
    MdOutlineEventAvailable
} from "react-icons/md";

import "../styles/UserHearings.css";

/* ======================
   HEARING STATUS CONFIGURATION
====================== */
const HEARING_STATUS_CONFIG = {
    INCOMPLETE: {
        label: "Incomplete",
        icon: <MdOutlinePendingActions size={14} />,
        color: "#f59e0b",
        bgColor: "#fef3c7",
        borderColor: "#fde68a"
    },
    COMPLETED: {
        label: "Completed",
        icon: <FiCheckCircle size={14} />,
        color: "#16a34a",
        bgColor: "#dcfce7",
        borderColor: "#bbf7d0"
    },
    CANCELLED: {
        label: "Cancelled",
        icon: <MdOutlineCancel size={14} />,
        color: "#dc2626",
        bgColor: "#fee2e2",
        borderColor: "#fecaca"
    }
};

export default function UserHearings() {
    const { token } = useAuth();
    const [hearings, setHearings] = useState([]);
    const [activeTab, setActiveTab] = useState("INCOMPLETE");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchHearings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchHearings = async () => {
        try {
            setLoading(true);
            const res = await fetch(
                `${API_BASE_URL}/api/cases/hearings/calendar`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (!res.ok) throw new Error("Failed to fetch hearings");

            const data = await res.json();
            setHearings(data.hearings || []);
        } catch (err) {
            console.error("Fetch hearings failed:", err);
            toast.error("Failed to load hearings");
        } finally {
            setLoading(false);
        }
    };

    /* ======================
        STAT COUNTS
    ====================== */
    const incompleteCount = hearings.filter(h => h.statusAtHearing === "INCOMPLETE").length;
    const completedCount = hearings.filter(h => h.statusAtHearing === "COMPLETED").length;
    const cancelledCount = hearings.filter(h => h.statusAtHearing === "CANCELLED").length;
    const totalCount = hearings.length;

    /* ======================
        FILTERED HEARINGS
    ====================== */
    const filteredHearings = hearings.filter(h => {
        const matchStatus = h.statusAtHearing === activeTab;

        const keyword = (
            (h.userDetails?.name || "") +
            (h.courtDetails?.courtName || "") +
            (h.courtDetails?.judgeName || "") +
            (h.caseDetails?.caseId || "")
        ).toLowerCase();

        const matchSearch = search ? keyword.includes(search.toLowerCase()) : true;

        return matchStatus && matchSearch;
    });

    /* ======================
        RENDER STATUS BADGE
    ====================== */
    const renderStatusBadge = (status) => {
        const config = HEARING_STATUS_CONFIG[status] || {
            label: status,
            icon: <FiAlertCircle size={14} />,
            color: "#6b7280",
            bgColor: "#f3f4f6",
            borderColor: "#e5e7eb"
        };

        return (
            <span
                className="user-hearings-status-badge"
                style={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    border: `1px solid ${config.borderColor}`
                }}
            >
                {config.icon}
                <span>{config.label}</span>
            </span>
        );
    };

    return (
        <div className="premium-user-hearings">
            {/* ======================
               HEADER
      ====================== */}
            <div className="user-hearings-header">
                <div className="user-hearings-header-left">
                    <h1 className="user-hearings-page-title">My Hearings</h1>
                    <p className="user-hearings-page-subtitle">
                        Track and manage your court hearings and schedules
                    </p>
                </div>
                <div className="user-hearings-header-right">
                    <button
                        className="user-hearings-refresh-btn"
                        onClick={fetchHearings}
                        disabled={loading}
                    >
                        <FiRefreshCw size={18} className={loading ? "spinning" : ""} />
                        <span>{loading ? "Refreshing..." : "Refresh"}</span>
                    </button>
                </div>
            </div>

            {/* ======================
               STATS CARDS
      ====================== */}
            <div className="user-hearings-stats-grid">
                <div className="user-hearings-stat-card user-hearings-stat-incomplete">
                    <div className="user-hearings-stat-icon">
                        <MdOutlinePendingActions size={24} />
                    </div>
                    <div className="user-hearings-stat-content">
                        <h3 className="user-hearings-stat-value">{incompleteCount}</h3>
                        <p className="user-hearings-stat-label">Incomplete</p>
                        <span className="user-hearings-stat-subtext">Pending Hearings</span>
                    </div>
                </div>

                <div className="user-hearings-stat-card user-hearings-stat-completed">
                    <div className="user-hearings-stat-icon">
                        <MdOutlineCheckCircleOutline size={24} />
                    </div>
                    <div className="user-hearings-stat-content">
                        <h3 className="user-hearings-stat-value">{completedCount}</h3>
                        <p className="user-hearings-stat-label">Completed</p>
                        <span className="user-hearings-stat-subtext">Finished Hearings</span>
                    </div>
                </div>

                <div className="user-hearings-stat-card user-hearings-stat-cancelled">
                    <div className="user-hearings-stat-icon">
                        <MdOutlineCancel size={24} />
                    </div>
                    <div className="user-hearings-stat-content">
                        <h3 className="user-hearings-stat-value">{cancelledCount}</h3>
                        <p className="user-hearings-stat-label">Cancelled</p>
                        <span className="user-hearings-stat-subtext">Cancelled Hearings</span>
                    </div>
                </div>

                <div className="user-hearings-stat-card user-hearings-stat-total">
                    <div className="user-hearings-stat-icon">
                        <FiFolder size={24} />
                    </div>
                    <div className="user-hearings-stat-content">
                        <h3 className="user-hearings-stat-value">{totalCount}</h3>
                        <p className="user-hearings-stat-label">Total Hearings</p>
                        <span className="user-hearings-stat-subtext">All Scheduled</span>
                    </div>
                </div>
            </div>

            {/* ======================
               MAIN CONTENT
      ====================== */}
            <div className="user-hearings-content">
                {/* Search Bar */}
                <div className="user-hearings-search-section">
                    <div className="user-hearings-search-container">
                        <FiSearch size={18} />
                        <input
                            type="text"
                            placeholder="Search by court, judge, or case details..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="user-hearings-search-input"
                        />
                        {search && (
                            <button
                                className="user-hearings-clear-search"
                                onClick={() => setSearch("")}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Simple Tabs */}
                <div className="user-hearings-tabs-section">
                    <div className="user-hearings-main-tabs">
                        <button
                            className={`user-hearings-tab-btn ${activeTab === "INCOMPLETE" ? "user-hearings-tab-active" : ""}`}
                            onClick={() => setActiveTab("INCOMPLETE")}
                        >
                            <MdOutlinePendingActions size={16} />
                            <span>Incomplete ({incompleteCount})</span>
                        </button>

                        <button
                            className={`user-hearings-tab-btn ${activeTab === "COMPLETED" ? "user-hearings-tab-active" : ""}`}
                            onClick={() => setActiveTab("COMPLETED")}
                        >
                            <MdOutlineCheckCircleOutline size={16} />
                            <span>Completed ({completedCount})</span>
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="user-hearings-table-section">
                    {loading ? (
                        <div className="user-hearings-loading-state">
                            <div className="user-hearings-loading-spinner"></div>
                            <p>Loading hearings...</p>
                        </div>
                    ) : filteredHearings.length === 0 ? (
                        <div className="user-hearings-empty-state">
                            <MdOutlineEventAvailable size={48} />
                            <p>No hearings found</p>
                            <small>Try adjusting your search or check back later</small>
                        </div>
                    ) : (
                        <div className="user-hearings-table-container">
                            <div className="user-hearings-table-wrapper">
                                <table className="user-hearings-table">
                                    <thead>
                                        <tr>
                                            <th className="user-hearings-table-header">
                                                <div className="user-hearings-table-header-content">
                                                    <FiUser size={14} />
                                                    <span>DETAILS</span>
                                                </div>
                                            </th>
                                            <th className="user-hearings-table-header">
                                                <div className="user-hearings-table-header-content">
                                                    <FiCalendar size={14} />
                                                    <span>HEARING DATE & TIME</span>
                                                </div>
                                            </th>
                                            <th className="user-hearings-table-header">
                                                <div className="user-hearings-table-header-content">
                                                    <FiMapPin size={14} />
                                                    <span>COURT DETAILS</span>
                                                </div>
                                            </th>
                                            <th className="user-hearings-table-header">
                                                <div className="user-hearings-table-header-content">
                                                    <FiBriefcase size={14} />
                                                    <span>SOLICITOR DETAILS</span>
                                                </div>
                                            </th>
                                            <th className="user-hearings-table-header">
                                                <div className="user-hearings-table-header-content">
                                                    <FiInfo size={14} />
                                                    <span>STATUS & REMARKS</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredHearings.map(h => (
                                            <tr key={h._id} className="user-hearings-table-row">
                                                {/* CLIENT & CASE DETAILS */}
                                                <td className="user-hearings-table-cell user-hearings-client-cell">
                                                    <div className="user-hearings-client-info">
                                                        <div className="user-hearings-client-avatar">
                                                            {h.userId?.userProfile?.photo ? (
                                                                <img
                                                                    src={h.userId.userProfile.photo}
                                                                    alt="client"
                                                                    className="user-hearings-client-avatar-img"
                                                                />
                                                            ) : (
                                                                <div className="user-hearings-client-avatar-placeholder">
                                                                    <FiUser size={16} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="user-hearings-client-details">
                                                            <strong className="user-hearings-client-name">
                                                                {h.userDetails?.name || "N/A"}
                                                            </strong>
                                                            <div className="user-hearings-contact-info">
                                                                <span className="user-hearings-contact-item">
                                                                    <FiMail size={12} />
                                                                    {h.userDetails?.email || "No email"}
                                                                </span>
                                                                <span className="user-hearings-contact-item">
                                                                    <FiPhone size={12} />
                                                                    {h.userDetails?.mobile ?
                                                                        `${h.userDetails.countryCode || ''} ${h.userDetails.mobile}` :
                                                                        "No phone"}
                                                                </span>
                                                            </div>
                                                            {h.caseId && (
                                                                <div className="user-hearings-case-ref">
                                                                    <FiFileText size={12} />
                                                                    <strong>Case ID:</strong>
                                                                    <span>{h.caseId}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* HEARING DATE & TIME */}
                                                <td className="user-hearings-table-cell">
                                                    <div className="user-hearings-datetime-cell">
                                                        <div className="user-hearings-date">
                                                            <FiCalendar size={14} />
                                                            <span>{new Date(h.hearingDateTime).toLocaleDateString('en-US', {
                                                                weekday: 'short',
                                                                year: 'numeric',
                                                                month: 'short',
                                                                day: 'numeric'
                                                            })}</span>
                                                        </div>
                                                        <div className="user-hearings-time">
                                                            <FiClock size={14} />
                                                            <span>{new Date(h.hearingDateTime).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit',
                                                                hour12: true
                                                            })}</span>
                                                        </div>
                                                        <div className="user-hearings-date-relative">
                                                            <small>
                                                                {(() => {
                                                                    const now = new Date();
                                                                    const hearingDate = new Date(h.hearingDateTime);
                                                                    const diffTime = hearingDate - now;
                                                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                                                    if (diffDays === 0) return "Today";
                                                                    if (diffDays === 1) return "Tomorrow";
                                                                    if (diffDays > 0) return `In ${diffDays} days`;
                                                                    if (diffDays === -1) return "Yesterday";
                                                                    return `${Math.abs(diffDays)} days ago`;
                                                                })()}
                                                            </small>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* COURT DETAILS */}
                                                <td className="user-hearings-table-cell">
                                                    <div className="user-hearings-court-cell">
                                                        <strong className="user-hearings-court-name">
                                                            {h.courtDetails?.courtName || "Court Not Specified"}
                                                        </strong>
                                                        <div className="user-hearings-court-details">
                                                            <span className="user-hearings-judge">
                                                                <FiUser size={12} />
                                                                {h.courtDetails?.judgeName || "Judge Not Assigned"}
                                                            </span>
                                                            <span className="user-hearings-location">
                                                                <FiMapPin size={12} />
                                                                {h.courtDetails?.courtLocation || "Location Not Specified"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* SOLICITOR DETAILS */}
                                                <td className="user-hearings-table-cell">
                                                    <div className="user-hearings-solicitor-cell">
                                                        {h.solicitorDetails ? (
                                                            <>
                                                                <strong className="user-hearings-solicitor-name">
                                                                    <FiUser size={12} />
                                                                    {h.solicitorDetails.name || "Solicitor Not Assigned"}
                                                                </strong>
                                                                <div className="user-hearings-solicitor-contact">
                                                                    <span className="user-hearings-solicitor-email">
                                                                        <FiMail size={11} />
                                                                        {h.solicitorDetails.email || "No email"}
                                                                    </span>
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <div className="user-hearings-no-solicitor">
                                                                <FiUser size={14} />
                                                                <span>No Solicitor Assigned</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* STATUS & REMARKS */}
                                                <td className="user-hearings-table-cell">
                                                    <div className="user-hearings-status-remarks-cell">
                                                        <div className="user-hearings-status-cell">
                                                            {renderStatusBadge(h.statusAtHearing)}
                                                            <div className="user-hearings-status-note">
                                                                <small>
                                                                    Updated: {new Date(h.updatedAt || h.createdAt || h.hearingDateTime).toLocaleDateString()}
                                                                </small>
                                                            </div>
                                                        </div>
                                                        <div className="user-hearings-remarks-section">
                                                            <strong>Remarks:</strong>
                                                            <p className="user-hearings-remarks-text">
                                                                {h.courtDetails?.remarks || "No remarks provided"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}